'use client';
import {useMemo} from 'react';
import {Checkbox, TableSortLabel, Typography} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Link, useLocation} from 'react-router-dom';
import {ParametersForTable} from '../../../schemas/ParametersForTable';
import {BasePartParameterValue} from '../../../schemas/BasePartParameterValue';
import {MultiOctoPartsResponse} from '../../../schemas/MultiOctoPartsResponse';
import {DetectedUniquePart} from '../../../schemas/DetectedUniquePart';
import {TableColumnsType} from 'antd';
import {DisplayName} from '../../../schemas/DisplayName';
import {ColumnType} from 'antd/es/table';
import {PIMCORE_CONTENT_BASE_URL} from '../../../utils/Pimcore';
import {removeCurlyBracesContent} from '../singleProductPageComponents/SingleProductPage';
import IconContainer from '../tableComponents/IconContainer';
import {getParameterFormattedValueByParameterIdSupportLanguage} from '../../../utils/ParameterRelatedFunction';
import {addComma} from '../../../utils/StringRelated';
import {ProductsContent} from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import {Brand} from "@/graphql/pimcore/modules/resourceLibrary/types/Brand";

export function getImageUrl(imagePath: string | null | undefined): string {
    let finalUrl = '';
    if (!imagePath) {
        finalUrl = '/static/product_image/ceramic.png';
    } else if (imagePath.startsWith('https://')) {
        finalUrl = imagePath;
    } else {
        finalUrl = `https://search.kemet.com/component-images/${imagePath}`;
    }
    return finalUrl;
}

interface Props {
    locale: string;
    language: string;
    parameters: ParametersForTable[] | undefined;
    ascColumn: string;
    descColumn: string;
    compareItemPns: string[];
    onToggleCompareItem: (pn: string, checked: boolean, full: BasePartParameterValue) => void;
    multiOctoPartsResponse: MultiOctoPartsResponse[] | undefined;
    detectedUniquePartsMap: Record<string, DetectedUniquePart>;
    handlePartNumberClick: (partNumber: string) => void;
    handleSortRequest: (columnName: string) => void;
    contentTree: ProductsContent;
    brands: Brand[];
}

export function useProductTableColumns({
    locale,
    language,
    parameters,
    ascColumn,
    descColumn,
    compareItemPns,
    onToggleCompareItem,
    multiOctoPartsResponse,
    detectedUniquePartsMap,
    handlePartNumberClick,
    handleSortRequest,
    contentTree,
    brands,
}: Props): TableColumnsType<BasePartParameterValue> {
    const location = useLocation();

    const getDynamicWidth = (displayName: string): number => {
        if (displayName.length < 6) return 150;
        if (displayName.length < 9) return 180;
        return 170;
    };

    const normalizeBrandName = (str: string | null | undefined) =>
        str
            ?.toLowerCase()
            .trim()
            .replace(/\s+/g, ' ') || '';   // 多空白變一個

    const brandMap = useMemo(() => {
        const map = new Map<string, Brand>();
        brands.forEach((b) => {
            const normalizedKey = normalizeBrandName(b.key);

            // 只有在有 logo.fullpath 的時候才塞進 map，避免 null 問題
            if (normalizedKey && b.logo?.fullpath) {
                map.set(normalizedKey, b);
            }
        });
        return map;
    }, [brands]);

    const renderTitle = (displayName: string, displayNames: DisplayName[], sortColumn: string) => {
        const isDesc = descColumn === sortColumn;
        const isAsc = ascColumn === sortColumn;
        let direction: 'asc' | 'desc' = 'asc';

        if (isDesc) {
            direction = 'desc';
        } else if (isAsc) {
            direction = 'asc';
        }

        // 根據 language 查找對應的 display 值
        const localizedDisplayName =
            displayNames?.find((dn) => dn.language === language)?.display || displayName;

        return (
            <div
                style={{
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: '0 8px',
                }}
            >
                {localizedDisplayName}
                {displayName !== 'Stock' && (
                    <TableSortLabel
                        active={true}
                        direction={direction}
                        onClick={() => handleSortRequest(sortColumn)}
                        IconComponent={ArrowDropDownIcon}
                        sx={{
                            '& .MuiTableSortLabel-icon': {
                                fontSize: '20px',
                                color: 'white !important',
                            },
                            '&.Mui-active': {
                                color: 'white',
                                '&& .MuiTableSortLabel-icon': {
                                    color: 'white',
                                },
                            },
                        }}
                    />
                )}
            </div>
        );
    };

    const baseColumns: ColumnType<BasePartParameterValue>[] = useMemo(() => {
        // console.groupCollapsed('[Columns] baseColumns recomputed');
        // console.log('locale            :', locale);
        // console.log('detectedUniqueMap :', Object.keys(detectedUniquePartsMap).length, 'keys');
        //  ⚠️ compareItemPns 已經拔掉，理論上這裡不該因勾選動作而重跑
        // console.groupEnd();

        return [
            /* 原本的兩個欄位定義 */
            {
                title: () => <span>{contentTree.comparison.compareButtonLabel.content}</span>,
                width: 120,
                dataIndex: 'compare',
                key: 'compare',
                fixed: 'left',
                align: 'center',
                render: (_, record) => (
                    <Checkbox
                        inputProps={{ 'aria-label': 'secondary checkbox' }}
                        sx={{
                            color: '#E5673E',
                            '&.Mui-checked': { color: '#E5673E' },
                            // hover 時（未勾選）顯示透明背景+橘色邊框
                            '&:hover:not(.Mui-checked)::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            },
                            // hover 時（未勾選）在中間顯示橘色勾勾
                            '&:hover:not(.Mui-checked)::after': {
                                content: '"✓"', // Unicode 勾選符號
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#E5673E',
                                fontSize: '1rem', // 根據需求調整
                                transition: 'all 300ms ease-out',
                            },
                        }}
                        onChange={(e) =>
                            onToggleCompareItem(record.basePn, e.target.checked, record)
                        }
                        checked={compareItemPns.includes(record.basePn)}
                    />
                ),
            },
            {
                title: `${contentTree.productsTable.columnLabels.partNumber.content}`,
                width: 340,
                dataIndex: 'partNumber',
                key: 'partNumber',
                fixed: 'left',
                align: 'center',
                render: (_, record) => {
                    const {
                        basePn: partNumber,
                        displayPn,
                        dataSheet,
                        imagePath,
                        rohs,
                        ksimSupport,
                        spiceFile,
                        step,
                        manufacturer,
                        specsheetSuppression,
                        description,
                        alias,
                        parameterValues,
                    } = record;

                    const normalizedManufacturer = normalizeBrandName(manufacturer);

                    const brand = normalizedManufacturer
                        ? brandMap.get(normalizedManufacturer)
                        : undefined;

                    const brandLogoUrl =
                        brand?.logo?.fullpath
                            ? `${PIMCORE_CONTENT_BASE_URL}${brand.logo.fullpath}`
                            : null;

                    return (
                        <div
                            data-testid={`${partNumber}_zone`}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                cursor: 'default',
                            }}
                        >
                            <div
                                className="image-div"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: 60,
                                    height: 60,
                                    cursor: 'pointer',
                                }}
                                onClick={() => handlePartNumberClick(partNumber)}
                            >
                                <img
                                    style={{objectFit: 'contain'}}
                                    src={getImageUrl(imagePath)}
                                    alt={'part_image'}
                                />
                                {brandLogoUrl && (
                                    <img
                                        src={brandLogoUrl}
                                        alt={manufacturer??""}
                                        style={{ width: '100%' }}
                                    />
                                )}
                            </div>
                            <div className="basePn-and-images" style={{marginLeft: '.5rem'}}>
                            <Typography
                                    component={Link}
                                    to={`${location.pathname}/part/${encodeURIComponent(partNumber)}`}
                                    sx={{
                                        color: '#B75332',
                                        fontWeight: 700,
                                        fontFamily: 'Poppins',
                                        cursor: 'pointer',
                                        display: 'grid', // ← 舊版有
                                        justifyContent: 'left',
                                    }}
                                >
                                    {removeCurlyBracesContent(displayPn)}
                                </Typography>
                                {alias && (
                                    <div
                                        data-testid={`${partNumber}_alias`}
                                        style={{
                                            display: 'grid', // ← 讓內容撐滿並保持左對齊
                                            justifyContent: 'left', // ← 與舊版一致
                                            marginTop: '1px',
                                        }}
                                    >
                                        ({alias})
                                    </div>
                                )}

                                {description ? (
                                    <Typography
                                        align="left"
                                        data-testid={`${partNumber}_description`}
                                        sx={{
                                            fontSize: '0.9375rem',
                                            fontFamily: 'Poppins',
                                            color: '#454545',
                                            marginTop: '2px',
                                            display: 'grid',
                                            justifyContent: 'left',
                                            minHeight: '22px',
                                        }}
                                    >
                                        {description}
                                    </Typography>
                                ) : (
                                    !alias && (
                                        <Typography
                                            align="left"
                                            data-testid={`${partNumber}_description`}
                                            sx={{
                                                fontSize: '0.9375rem',
                                                fontFamily: 'Poppins',
                                                color: '#454545',
                                                marginTop: '2px',
                                                display: 'grid',
                                                justifyContent: 'left',
                                                minHeight: '22px',
                                            }}
                                        >
                                            {'\u00A0'}
                                        </Typography>
                                    )
                                )}

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        paddingTop: '0.5rem',
                                    }}
                                >
                                    <IconContainer
                                        dataSheet={dataSheet ?? ''}
                                        partNumber={encodeURIComponent(partNumber)}
                                        rohs={rohs}
                                        ksimSupport={ksimSupport ?? false}
                                        spiceFile={spiceFile ?? false}
                                        step={step ?? ''}
                                        manufacturer={manufacturer ?? ''}
                                        specsheetSuppression={specsheetSuppression ?? false}
                                        unQualifyInfo={
                                            detectedUniquePartsMap[partNumber]?.message?.text
                                        }
                                        parameterValues={parameterValues}
                                        contentTree={contentTree}
                                        locale={locale}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        ];
        // ⬇︎ 依賴陣列
    }, [locale, compareItemPns, detectedUniquePartsMap, contentTree]);

    const parameterColumns: ColumnType<BasePartParameterValue>[] = useMemo(() => {
        // console.time('[Columns] parameterColumns recomputed');
        if (!parameters) return [];

        return parameters.map((param) => ({
            title: renderTitle(param.displayName, param.displayNames, param.id.toString()),
            dataIndex: param.name,
            key: param.name,
            align: 'center',
            width: getDynamicWidth(param.displayName),
            render: (_: any, record) => {
                if (param.name === 'Stock') {
                    const octo = multiOctoPartsResponse?.find((part) => part.pn === record.basePn);
                    const total = octo?.filteredResult.reduce((acc, p) => acc + p.quantity, 0) ?? 0;
                    return total.toLocaleString();
                }

                const raw = getParameterFormattedValueByParameterIdSupportLanguage(
                    record.parameterValues,
                    param.id,
                    language
                );

                const rawTrim = raw.trim();

                // 純數字或小數，但排除像 0204 這種有前導 0 的代碼（允許 0.x 小數）
                const isNumberLike = /^-?\d+(\.\d+)?$/.test(rawTrim) && !/^0\d+/.test(rawTrim); // 排除 0204、0010 等

                return isNumberLike ? addComma(rawTrim) : raw;
            },
        }));
        // console.timeEnd('[Columns] parameterColumns recomputed');
    }, [parameters, language, multiOctoPartsResponse]);

    return useMemo(() => {
        const merged = [...baseColumns, ...parameterColumns];
        // console.log('[Columns] merged length:', merged.length);
        return merged;
    }, [baseColumns, parameterColumns]);
}
